import React, { useState, useEffect, useRef } from 'react';
import { FaUserMd, FaUserInjured, FaMicrochip, FaPlus, FaSearch, FaClipboardList, FaEnvelope, FaPhone, FaClock } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import HospitalNavbar from './HospitalNavbar';
import './HospitalDashboard.css';

const HospitalDashboard = () => {
  const navigate = useNavigate();
  const [selectedCard, setSelectedCard] = useState(null);
  const [showAddDoctor, setShowAddDoctor] = useState(false);
  const [newDoctorData, setNewDoctorData] = useState({
    name: '',
    specialization: '',
    email: '',
    phone: '',
    experience: '',
    availability: ''
  });
  const [doctors, setDoctors] = useState([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const modalRef = useRef(null);
  const addDoctorRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closeModal();
      }
      if (addDoctorRef.current && !addDoctorRef.current.contains(event.target)) {
        closeAddDoctor();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (doctors.length > 0) {
      const filtered = doctors.filter(doctor => 
        doctor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doctor.specialization.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doctor.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredDoctors(filtered);
    }
  }, [searchQuery, doctors]);

  const handleAddNewDoctor = () => {
    setShowAddDoctor(true);
    setSelectedCard(null); // Close the features modal
  };

  const closeAddDoctor = () => {
    setShowAddDoctor(false);
    setNewDoctorData({
      name: '',
      specialization: '',
      email: '',
      phone: '',
      experience: '',
      availability: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewDoctorData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitDoctor = (e) => {
    e.preventDefault();
    const newDoctor = {
      id: Date.now(), // temporary id, replace with actual ID from backend
      ...newDoctorData
    };
    setDoctors(prevDoctors => [...prevDoctors, newDoctor]);
    closeAddDoctor();
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const stats = [
    { 
      title: 'Doctors', 
      count: 48, 
      icon: <FaUserMd />, 
      color: '#3498db',
      path: '/doctors',
      description: 'View and manage hospital doctors',
      features: [
        { icon: <FaPlus />, title: 'Add New Doctor', action: handleAddNewDoctor },
        { icon: <FaSearch />, title: 'Search Doctors', action: () => navigate('/doctors/search') },
        { icon: <FaClipboardList />, title: 'View All Doctors', action: () => navigate('/doctors') }
      ]
    },
    { 
      title: 'Devices', 
      count: 48, 
      icon: <FaMicrochip />, 
      color: '#9b59b6',
      path: '/devices',
      description: 'Monitor connected medical devices',
      features: [
        { icon: <FaPlus />, title: 'Add New Device', action: () => navigate('/devices/add') },
        { icon: <FaSearch />, title: 'Search Devices', action: () => navigate('/devices/search') },
        { icon: <FaClipboardList />, title: 'View All Devices', action: () => navigate('/devices') }
      ]
    },
    { 
      title: 'Patients', 
      count: 1243, 
      icon: <FaUserInjured />, 
      color: '#2ecc71',
      path: '/patients',
      description: 'Access patient records and information',
      features: [
        { icon: <FaPlus />, title: 'Register New Patient', action: () => navigate('/patients/register') },
        { icon: <FaSearch />, title: 'Search Patients', action: () => navigate('/patients/search') },
        { icon: <FaClipboardList />, title: 'View All Patients', action: () => navigate('/patients') }
      ]
    }
  ];

  const handleCardClick = (stat) => {
    setSelectedCard(stat);
  };

  const closeModal = () => {
    setSelectedCard(null);
  };

  return (
    <>
      <HospitalNavbar />
      <div className="dashboard-container">
        <div className="dashboard-content">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search doctors..."
              value={searchQuery}
              onChange={handleSearch}
              className="search-input"
            />
          </div>

          <div className="stats-container">
            {stats.map((stat, index) => (
              <div 
                className="stat-card" 
                key={index} 
                onClick={() => handleCardClick(stat)}
                role="button"
                tabIndex={0}
                aria-label={`View ${stat.title.toLowerCase()}`}
              >
                <div className="stat-icon" style={{ backgroundColor: stat.color }}>
                  {stat.icon}
                </div>
                <div className="stat-details">
                  <h3>{stat.title}</h3>
                  <p className="stat-count">{stat.count}</p>
                  <p className="stat-description">{stat.description}</p>
                </div>
              </div>
            ))}
          </div>

          {filteredDoctors.length > 0 && (
            <div className="doctors-section">
              <h2>Search Results</h2>
              <div className="doctors-grid">
                {filteredDoctors.map(doctor => (
                  <div key={doctor.id} className="doctor-card">
                    <div className="doctor-card-header">
                      <div className="doctor-avatar">
                        <FaUserMd />
                      </div>
                      <div className="doctor-info-header">
                        <h3>{doctor.name}</h3>
                        <span className="specialization">{doctor.specialization}</span>
                      </div>
                    </div>
                    <div className="doctor-details">
                      <div className="detail-item">
                        <FaEnvelope />
                        <span>{doctor.email}</span>
                      </div>
                      <div className="detail-item">
                        <FaPhone />
                        <span>{doctor.phone}</span>
                      </div>
                      <div className="detail-item">
                        <FaClock />
                        <span>{doctor.availability}</span>
                      </div>
                      <div className="detail-item">
                        <FaUserMd />
                        <span>{doctor.experience} Experience</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {doctors.length > 0 && (
            <div className="doctors-section">
              <h2>Recently Added Doctors</h2>
              <div className="doctors-grid">
                {doctors.map(doctor => (
                  <div key={doctor.id} className="doctor-card">
                    <div className="doctor-card-header">
                      <div className="doctor-avatar">
                        <FaUserMd />
                      </div>
                      <div className="doctor-info-header">
                        <h3>{doctor.name}</h3>
                        <span className="specialization">{doctor.specialization}</span>
                      </div>
                    </div>
                    <div className="doctor-details">
                      <div className="detail-item">
                        <FaEnvelope />
                        <span>{doctor.email}</span>
                      </div>
                      <div className="detail-item">
                        <FaPhone />
                        <span>{doctor.phone}</span>
                      </div>
                      <div className="detail-item">
                        <FaClock />
                        <span>{doctor.availability}</span>
                      </div>
                      <div className="detail-item">
                        <FaUserMd />
                        <span>{doctor.experience} Experience</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showSuccessMessage && (
            <div className="success-message">
              Doctor added successfully!
            </div>
          )}

          {selectedCard && (
            <div className="modal-overlay">
              <div className="feature-modal" ref={modalRef}>
                <div className="modal-header">
                  <h2>{selectedCard.title}</h2>
                  <button className="close-modal" onClick={closeModal}>&times;</button>
                </div>
                <div className="modal-content">
                  <div className="features-grid">
                    {selectedCard.features.map((feature, index) => (
                      <button 
                        key={index} 
                        className="feature-button"
                        onClick={feature.action}
                      >
                        <span className="feature-icon">{feature.icon}</span>
                        <span className="feature-title">{feature.title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {showAddDoctor && (
            <div className="modal-overlay">
              <div className="add-doctor-modal" ref={addDoctorRef}>
                <div className="modal-header">
                  <h2>Add New Doctor</h2>
                  <button className="close-modal" onClick={closeAddDoctor}>&times;</button>
                </div>
                <div className="modal-content">
                  <form onSubmit={handleSubmitDoctor} className="add-doctor-form">
                    <div className="form-grid">
                      <div className="form-group">
                        <label htmlFor="name">Doctor Name</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={newDoctorData.name}
                          onChange={handleInputChange}
                          placeholder="Enter doctor's full name"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="specialization">Specialization</label>
                        <input
                          type="text"
                          id="specialization"
                          name="specialization"
                          value={newDoctorData.specialization}
                          onChange={handleInputChange}
                          placeholder="Enter specialization"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={newDoctorData.email}
                          onChange={handleInputChange}
                          placeholder="Enter email address"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="phone">Phone Number</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={newDoctorData.phone}
                          onChange={handleInputChange}
                          placeholder="Enter phone number"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="experience">Experience</label>
                        <input
                          type="text"
                          id="experience"
                          name="experience"
                          value={newDoctorData.experience}
                          onChange={handleInputChange}
                          placeholder="Enter years of experience"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="availability">Availability</label>
                        <input
                          type="text"
                          id="availability"
                          name="availability"
                          value={newDoctorData.availability}
                          onChange={handleInputChange}
                          placeholder="Enter availability schedule"
                          required
                        />
                      </div>
                    </div>
                    <div className="form-actions">
                      <button type="submit" className="submit-btn">Add Doctor</button>
                      <button type="button" className="cancel-btn" onClick={closeAddDoctor}>Cancel</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default HospitalDashboard;
